import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface StudentTransportProps {
  domain: string;
}

interface StudentTransportRecord {
  id: string;
  studentId: string;
  studentName: string;
  class: string;
  routeNumber: string;
  routeName: string;
  pickupPoint: string;
  dropPoint: string;
  vehicleNumber: string;
  transportType: 'Two-way' | 'Pick-up Only' | 'Drop Only';
  status: 'Active' | 'Inactive';
}

const StudentTransport: React.FC<StudentTransportProps> = ({ domain }) => {
  const [transportRecords, setTransportRecords] = useState<StudentTransportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<StudentTransportRecord | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRoute, setFilterRoute] = useState('All');
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    class: '',
    routeNumber: '',
    routeName: '',
    pickupPoint: '',
    dropPoint: '',
    vehicleNumber: '',
    transportType: 'Two-way' as 'Two-way' | 'Pick-up Only' | 'Drop Only',
    status: 'Active' as 'Active' | 'Inactive',
  });

  useEffect(() => {
    fetchTransportRecords();
  }, [domain]);

  const fetchTransportRecords = async () => {
    try {
      setLoading(true);
      const recordsRef = collection(db, `school-users/${domain}/student-transport`);
      const snapshot = await getDocs(recordsRef);
      const records: StudentTransportRecord[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as StudentTransportRecord));
      setTransportRecords(records);
    } catch (error) {
      console.error('Error fetching transport records:', error);
      alert('Failed to fetch transport records');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.studentId || !formData.studentName || !formData.routeNumber || !formData.pickupPoint) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const recordData = { ...formData };

      if (editingRecord) {
        const recordRef = doc(db, `school-users/${domain}/student-transport`, editingRecord.id);
        await updateDoc(recordRef, recordData);
        alert('Transport record updated successfully!');
      } else {
        await addDoc(collection(db, `school-users/${domain}/student-transport`), recordData);
        alert('Student assigned to transport successfully!');
      }

      resetForm();
      fetchTransportRecords();
    } catch (error) {
      console.error('Error saving transport record:', error);
      alert('Failed to save transport record');
    }
  };

  const handleEdit = (record: StudentTransportRecord) => {
    setEditingRecord(record);
    setFormData({
      studentId: record.studentId,
      studentName: record.studentName,
      class: record.class,
      routeNumber: record.routeNumber,
      routeName: record.routeName,
      pickupPoint: record.pickupPoint,
      dropPoint: record.dropPoint,
      vehicleNumber: record.vehicleNumber,
      transportType: record.transportType,
      status: record.status,
    });
    setShowForm(true);
  };

  const handleDelete = async (recordId: string) => {
    if (!confirm('Are you sure you want to remove this student from transport?')) return;

    try {
      await deleteDoc(doc(db, `school-users/${domain}/student-transport`, recordId));
      alert('Student removed from transport successfully!');
      fetchTransportRecords();
    } catch (error) {
      console.error('Error deleting transport record:', error);
      alert('Failed to delete transport record');
    }
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      studentName: '',
      class: '',
      routeNumber: '',
      routeName: '',
      pickupPoint: '',
      dropPoint: '',
      vehicleNumber: '',
      transportType: 'Two-way',
      status: 'Active',
    });
    setEditingRecord(null);
    setShowForm(false);
  };

  const filteredRecords = transportRecords.filter(record => {
    const matchesSearch =
      record.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.routeNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRoute = filterRoute === 'All' || record.routeNumber === filterRoute;

    return matchesSearch && matchesRoute;
  });

  const uniqueRoutes = Array.from(new Set(transportRecords.map(r => r.routeNumber)));

  const getTransportTypeBadge = (type: string) => {
    switch (type) {
      case 'Two-way': return 'bg-success';
      case 'Pick-up Only': return 'bg-info';
      case 'Drop Only': return 'bg-warning';
      default: return 'bg-secondary';
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="bi bi-people me-2"></i>Student Transport Assignment
        </h5>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          <i className="bi bi-plus-circle me-2"></i>
          {showForm ? 'Cancel' : 'Assign Student'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h6 className="card-title mb-3">
              {editingRecord ? 'Edit Transport Assignment' : 'Assign Student to Transport'}
            </h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Student ID *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="studentId"
                    value={formData.studentId}
                    onChange={handleInputChange}
                    placeholder="e.g., STU-001"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Student Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleInputChange}
                    placeholder="e.g., Rahul Sharma"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Class</label>
                  <input
                    type="text"
                    className="form-control"
                    name="class"
                    value={formData.class}
                    onChange={handleInputChange}
                    placeholder="e.g., 10-A"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Route Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="routeNumber"
                    value={formData.routeNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., R-01"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Route Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="routeName"
                    value={formData.routeName}
                    onChange={handleInputChange}
                    placeholder="e.g., North Delhi Route"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Pickup Point *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pickupPoint"
                    value={formData.pickupPoint}
                    onChange={handleInputChange}
                    placeholder="e.g., Rohini Sector 10"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Drop Point</label>
                  <input
                    type="text"
                    className="form-control"
                    name="dropPoint"
                    value={formData.dropPoint}
                    onChange={handleInputChange}
                    placeholder="e.g., Same as pickup"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Vehicle Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., DL-1234"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Transport Type *</label>
                  <select
                    className="form-select"
                    name="transportType"
                    value={formData.transportType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Two-way">Two-way (Pick-up & Drop)</option>
                    <option value="Pick-up Only">Pick-up Only</option>
                    <option value="Drop Only">Drop Only</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <button type="submit" className="btn btn-primary me-2">
                  <i className="bi bi-check-circle me-2"></i>
                  {editingRecord ? 'Update Assignment' : 'Assign Student'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  <i className="bi bi-x-circle me-2"></i>Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-8">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by student name, ID, or route..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={filterRoute}
                onChange={(e) => setFilterRoute(e.target.value)}
              >
                <option value="All">All Routes</option>
                {uniqueRoutes.map(route => (
                  <option key={route} value={route}>{route}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Total Students</h6>
              <h3 className="mb-0">{transportRecords.length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Active</h6>
              <h3 className="mb-0 text-success">
                {transportRecords.filter(r => r.status === 'Active').length}
              </h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Routes</h6>
              <h3 className="mb-0">{uniqueRoutes.length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="text-muted mb-1">Two-way</h6>
              <h3 className="mb-0 text-info">
                {transportRecords.filter(r => r.transportType === 'Two-way').length}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Records Table */}
      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 d-block mb-3"></i>
              <p>No transport records found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Student ID</th>
                    <th>Student Name</th>
                    <th>Class</th>
                    <th>Route</th>
                    <th>Pickup Point</th>
                    <th>Drop Point</th>
                    <th>Vehicle</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td className="fw-semibold">{record.studentId}</td>
                      <td>{record.studentName}</td>
                      <td>{record.class || 'N/A'}</td>
                      <td>
                        <span className="badge bg-primary">{record.routeNumber}</span>
                        {record.routeName && <small className="d-block text-muted">{record.routeName}</small>}
                      </td>
                      <td>{record.pickupPoint}</td>
                      <td>{record.dropPoint || 'Same'}</td>
                      <td>{record.vehicleNumber || 'N/A'}</td>
                      <td>
                        <span className={`badge ${getTransportTypeBadge(record.transportType)}`}>
                          {record.transportType}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${record.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                          {record.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleEdit(record)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(record.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentTransport;
